import { Button as MUIButton, Stack, useTheme } from "@mui/material";
import type { ReactNode } from "react";

type ButtonProps = {
  type: "primary" | "secondary" | "tertiary";
  label?: ReactNode;
  iconStart?: ReactNode;
  iconEnd?: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  customWidth?: number;
  rounded?: boolean;
};

export const Button = ({
  type,
  label,
  iconStart,
  iconEnd,
  onClick,
  disabled = false,
  customWidth,
  rounded = false,
}: ButtonProps) => {
  const { colors, customSpacing, radius, customFontWeight } = useTheme();

  return (
    <MUIButton
      variant="outlined"
      onClick={onClick}
      disabled={disabled}
      sx={{
        whiteSpace: "nowrap",
        minWidth: 40,
        width: rounded ? 40 : customWidth ? `${customWidth}px` : "auto",
        height: customSpacing["4xl"],
        gap: customSpacing.none,
        borderColor:
          type === "primary"
            ? colors.background.brandPrimary
            : colors.border.primary,
        backgroundColor:
          type === "primary"
            ? colors.background.brandPrimary
            : type === "secondary"
              ? colors.background.primary
              : colors.background.hover,
        color: type === "primary" ? colors.text.inverse : colors.text.primary,
        borderRadius: radius.full,
        px: rounded ? 0 : customSpacing.lg,
        textTransform: "none",
        fontWeight: customFontWeight.bold,
        "&:hover": {
          backgroundColor: disabled
            ? undefined
            : type === "primary"
              ? colors.text.brandPrimaryHover
              : type === "secondary"
                ? colors.background.primary
                : colors.background.secondary,
          borderColor: disabled
            ? undefined
            : type === "secondary"
              ? colors.border.secondary
              : undefined,
        },
        "&:disabled": {
          opacity: 0.6,
          color: type === "primary" ? colors.text.inverse : undefined,
        },
      }}
    >
      {rounded ? (
        <Stack alignItems="center">{label}</Stack>
      ) : (
        <Stack direction="row" alignItems="center">
          {iconStart && <Stack alignItems="center">{iconStart}</Stack>}
          <Stack
            gap={customSpacing.md}
            px={customSpacing.md}
            alignItems="center"
          >
            {label}
          </Stack>
          {iconEnd && <Stack alignItems="center">{iconEnd}</Stack>}
        </Stack>
      )}
    </MUIButton>
  );
};
